import { makeAutoObservable, toJS } from 'mobx';
import moment from 'moment';
import { CardThemeType } from 'components/cards/card-type';
import { debitPaymentMethods, PaymentMethodType } from 'core/enums/payment-method';
import { SitePlatform } from 'core/enums/site-platform';
import { Account, AccountsDetailsDTO, AmountToPayDTO } from 'core/models/account/amount-to-pay.dto';
import { Country } from 'core/models/country';
import { AccountMahService } from 'core/services/api/account-mah';
import { AnalyticsStructureStore } from 'core/stores/analytics-structure.store';
import { CurrencyStore } from 'core/stores/currency.store';
import { SiteConfigStore } from 'core/stores/site-config.store';
import { NumberUtils } from 'utils/number.utils';

export enum AmountToPayLayout {
  StandardCard,
  CompactCard,
  Button,
}

type AmountInputValidation = {
  min: number,
  max: number,
}


export class AmountToPayStore {
  amount = {} as AmountToPayDTO;
  account!: Account;
  currencyStore!: CurrencyStore;
  dueDateUTC!: string;
  paymentMethod!: string;
  locale!: string;
  editedValue!: string | null;

  paymentInProgress = false;
  isEditingValue = false;

  isAmountToPayLoading = true;
  cardBackground!: CardThemeType;
  validation!: AmountInputValidation;

  nominalAmount = 1;

  get dueDate(): Date {
    return moment.utc(this.dueDateUTC).toDate();
  }

  get daysRemaining(): number {
    const days = moment.utc(this.dueDateUTC).diff(moment(), 'days');
    return days < 0 ? 0 : days;
  }

  get formattedValue(): string {
    return this.currencyStore?.getCurrencyValue(this.amount?.value || '0.00');
  }

  get isAmountBelowMin(): boolean {
    return !!this.validation && !!this.editedValue && NumberUtils.RemoveCommas(this.editedValue) < this.validation.min;
  }

  get isAmountAboveMax(): boolean {
    return !!this.validation && !!this.editedValue && NumberUtils.RemoveCommas(this.editedValue) > this.validation.max
  }

  get isValid(): boolean {
    return !this.isAmountBelowMin && !this.isAmountAboveMax;
  }

  get isLoading(): boolean {
    return this.isAmountToPayLoading;
  }

  get disablePayButton(): boolean {
    return !this.isValid ||
      (!this.amount?.value && !this.editedValue) ||
      ((!this.amount?.value || NumberUtils.RemoveCommas(this.amount.value) <= 0) &&
        (!this.editedValue || NumberUtils.RemoveCommas(this.editedValue) <= 0)) ||
      false
  }

  get isDebitOrder(): boolean {
    return debitPaymentMethods.includes(this.paymentMethod as PaymentMethodType);
  }

  constructor() {
    makeAutoObservable(this);
  }

  initialize(customerNumber: string, country: string, currencyStore: CurrencyStore, fetchMarketMinMaxValidations = false) {
    this.changeCurrencyStore(currencyStore)
    this.changeIsAmountToPayLoading(true);

    const minMaxValidationRequest = fetchMarketMinMaxValidations ? AccountMahService.getPaymentLimit(customerNumber, country) : Promise.resolve();

    return Promise.all([
      AccountMahService.getAmountToPay(customerNumber, country),
      minMaxValidationRequest
    ])
      .then(([data, validations]: [AccountsDetailsDTO, any]) => {
        const account = SiteConfigStore.getAccount(data?.accounts);

        if (!account) {
          console.error('Failed to retrieve account information');
          return;
        }

        this.changeAccount(account);
        this.changeAmountToPay(data?.totalAmountToPay);
        this.changePaymentMethod(account.methodOfPayment);
        this.changeDueDate(data?.dueDate);
        this.changeNominalAmount(data?.minimumRecPayableAmount ?? 1);

        if (validations?.limits) {
          const limits = validations.limits[currencyStore.currencyCode];

          if (limits) {
            this.changeValidation({
              min: limits.minimumAmount,
              max: limits.maximumAmount
            });
          }
        }
      })
      .catch(err => {
        console.error(err);
      })
      .finally(() => {
        this.changeIsAmountToPayLoading(false);
      });
  }

  setCardType(layout: AmountToPayLayout, platform: SitePlatform) {
    const isDStv = platform === SitePlatform.dstv
    switch (layout) {
      case AmountToPayLayout.StandardCard:
        this.changeCardBackground(isDStv ? CardThemeType.Light : CardThemeType.GlassSecondary);
        break;
      case AmountToPayLayout.CompactCard:
        this.changeCardBackground(isDStv ? CardThemeType.DarkGlass : CardThemeType.Glass);
        break;
    }
  }

  getValue(value: any) {
    return this.currencyStore.getCurrencyValue(value, false);
  }

  fetchAccountDetails(customerNumber: string, country: string) {
    AccountMahService.getAmountToPay(customerNumber, country)
      .then((data: AccountsDetailsDTO) => {
        const account = SiteConfigStore.getAccount(data?.accounts);

        if (!account) {
          console.error('Failed to retrieve account information');
          return;
        }
        this.changeAccount(account);
        this.changeAmountToPay(data?.totalAmountToPay);
        this.changePaymentMethod(account.methodOfPayment);
        this.changeDueDate(data?.dueDate);
      })
      .catch((err) => {
        console.error('Failed to get account info -> ', err);
      })
      .finally(() => this.changeIsAmountToPayLoading(false));
  }

  changeAmountToPay(amountToPay: AmountToPayDTO) {
    this.amount.value = amountToPay.value;
    // this.changeConvertedValue(amountToPay.value);
  }

  changeDueDate(dueDateUTC: string) {
    this.dueDateUTC = dueDateUTC;
  }

  changeValidation(validation: AmountInputValidation) {
    this.validation = validation;
  }

  changePaymentMethod(paymentMethod: string) {
    this.paymentMethod = paymentMethod;
  }

  changeIsAmountToPayLoading(isLoading: boolean) {
    this.isAmountToPayLoading = isLoading;
  }

  changeCardBackground(cardBackground: CardThemeType) {
    this.cardBackground = cardBackground;
  }

  changeCurrencyStore(currencyStore: CurrencyStore) {
    this.currencyStore = currencyStore;
  }

  changePaymentInProgress(isProcessing: boolean) {
    this.paymentInProgress = isProcessing;
  }

  changeAccount(account: Account) {
    this.account = account;
  }

  changeIsEditingValue(isEditingValue: boolean) {
    this.isEditingValue = isEditingValue;
    this.editedValue = null;
  }

  changeEditedValue(value: string) {
    this.editedValue = NumberUtils.formatNumberInputWithDecimals(value, true);
    AnalyticsStructureStore.updateProperty("amountToPay", this.editedValue);
  }

  changeNominalAmount(amount: number) {
    this.nominalAmount = amount;
  }
}